import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api";
import TopNav from "./layout/TopNav";
import useModalEscape from "@/hooks/useModalEscape";

const Profile = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stocktwitsUsername, setStocktwitsUsername] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({
    email: true,
    sms: true,
    in_app: true,
  });
  const [isSavingNotifs, setIsSavingNotifs] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Close delete modal with Escape
  useModalEscape(() => setShowDeleteModal(false), showDeleteModal);

  useEffect(() => {
    if (user?.stocktwits_username) {
      setStocktwitsUsername(user.stocktwits_username);
    }

    // Load notification preferences
    if (user?.notification_preferences) {
      setNotifPrefs({
        email: user.notification_preferences.email ?? true,
        sms: user.notification_preferences.sms ?? true,
        in_app: user.notification_preferences.in_app ?? true,
      });
    }
  }, [user]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await api.patch("/user/profile", {
        stocktwits_username: stocktwitsUsername || null,
      });

      setSuccessMessage("Profile updated successfully!");
      setIsEditing(false);

      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to update profile. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setStocktwitsUsername(user?.stocktwits_username || "");
    setIsEditing(false);
    setError(null);
  };

  const handleToggleNotification = async (channel) => {
    const newPrefs = {
      ...notifPrefs,
      [channel]: !notifPrefs[channel],
    };

    setNotifPrefs(newPrefs);

    try {
      setIsSavingNotifs(true);
      await api.patch("/user/notification-preferences", newPrefs);

      setSuccessMessage("Notification preferences updated!");

      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error updating notification preferences:", err);
      // Revert the change on error
      setNotifPrefs(notifPrefs);
      setError(
        err.response?.data?.detail ||
          "Failed to update notification preferences. Please try again.",
      );
    } finally {
      setIsSavingNotifs(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete("/users/me");

      // Log out and redirect to landing page
      logout();
      navigate("/");
    } catch (err) {
      console.error("Error deleting account:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to delete account. Please try again.",
      );
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <TopNav />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Profile Settings
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Manage your account information
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {successMessage}
                  </p>
                </div>
              )}

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Email cannot be changed
                </p>
              </div>

              {/* StockTwits Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  StockTwits Username
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={stocktwitsUsername}
                    onChange={(e) => setStocktwitsUsername(e.target.value)}
                    placeholder="Enter your StockTwits username"
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                ) : (
                  <div className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white">
                    {stocktwitsUsername || (
                      <span className="text-gray-400 dark:text-gray-500">
                        Not set
                      </span>
                    )}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Your StockTwits username (without the @ symbol)
                </p>
              </div>

              {/* Notification Preferences */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                  Notification Preferences
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Choose how you want to receive price alert notifications
                </p>

                <div className="space-y-4">
                  {/* Email Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <svg
                          className="w-5 h-5 text-blue-600 dark:text-blue-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Email Notifications
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Receive alerts via email
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleNotification("email")}
                      disabled={isSavingNotifs}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notifPrefs.email
                          ? "bg-blue-600"
                          : "bg-gray-200 dark:bg-gray-700"
                      } ${isSavingNotifs ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          notifPrefs.email ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* SMS Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg">
                        <svg
                          className="w-5 h-5 text-green-600 dark:text-green-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          SMS Notifications
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Receive alerts via text message
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleNotification("sms")}
                      disabled={isSavingNotifs}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notifPrefs.sms
                          ? "bg-blue-600"
                          : "bg-gray-200 dark:bg-gray-700"
                      } ${isSavingNotifs ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          notifPrefs.sms ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* In-App Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                        <svg
                          className="w-5 h-5 text-purple-600 dark:text-purple-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          In-App Notifications
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Show notifications in the app
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleNotification("in_app")}
                      disabled={isSavingNotifs}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notifPrefs.in_app
                          ? "bg-blue-600"
                          : "bg-gray-200 dark:bg-gray-700"
                      } ${isSavingNotifs ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          notifPrefs.in_app ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Subscription Tier Display */}
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Current Plan: {user?.subscription_tier || "FREE"}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {user?.subscription_tier === "FREE" &&
                          "10 active alerts"}
                        {user?.subscription_tier === "PREMIUM" &&
                          "50 active alerts"}
                        {user?.subscription_tier === "PRO" &&
                          "200 active alerts"}
                      </div>
                    </div>
                    {user?.subscription_tier === "FREE" && (
                      <button className="px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                        Upgrade
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Account Information
              </h2>
            </div>
            <div className="px-6 py-6">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Account Created
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {user?.created_at
                      ? new Date(user.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Login
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {user?.last_login_at
                      ? new Date(user.last_login_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          },
                        )
                      : "N/A"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow border-2 border-red-200 dark:border-red-900">
            <div className="px-6 py-4 border-b border-red-200 dark:border-red-900">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Danger Zone
              </h2>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-white">
                    Delete Account
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="ml-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete Account
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete your account? This will
                permanently delete:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4">
                <li>All trading accounts</li>
                <li>All transactions and holdings</li>
                <li>All price alerts and notifications</li>
                <li>All research and intel items</li>
                <li>All chat history</li>
              </ul>
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
